import { VolumeRange } from '@studio/design-system/audio/volume';
import { Lazy } from '@studio/js-utils/async';
import type { T_Any } from '@tryiris-ai/js-types';
import { toBoolean, toNumber } from '@tryiris-ai/js-types';
import { shallow } from 'zustand/shallow';
import { PrefKeys } from '../constants.js';
import type { Sound } from './types.js';
import { preferences } from '#~/preferences/index.js';
import type { State } from '#~/preferences/index.js';

type T_Gain = [number, boolean];

const getVolume = (µ: State): number =>
  toNumber(µ.getValue(PrefKeys.volume), { default: VolumeRange.max });

const isMuted = (µ: State): boolean => toBoolean(µ.getValue(PrefKeys.muted));

const adjustGain = (master: GainNode, [volume, muted]: T_Gain): void => {
  master.gain.value = muted ? 0 : volume;
};

/** Lazily-loaded sound singleton. */
const __sound = new Lazy((): Sound => {
  // Application-wide audio context
  const context = new window.AudioContext({
    latencyHint: 'balanced'
  });

  // Create master fader
  const master = context.createGain();

  // Connect the master fader to the audio context destination.
  master.connect(context.destination);

  // Set the initial master fader level.
  ((): void => {
    const state = preferences.getState();

    adjustGain(master, [getVolume(state), isMuted(state)]);
  })();

  // Subscribe to changes to the output volume.
  preferences.subscribe(
    (µ): T_Gain => [getVolume(µ), isMuted(µ)],
    (gain) => {
      adjustGain(master, gain);
    },
    {
      equalityFn: shallow
    }
  );

  // Watch for changes to the audio context state.
  context.addEventListener('statechange', () => {
    const state = preferences.getState();

    adjustGain(master, [getVolume(state), isMuted(state)]);
  });

  return {
    context,
    master
  };
});

/** Sound singleton. */
export const sound = new Proxy<Sound>(<T_Any>{}, {
  get(...args): unknown {
    return Reflect.get(__sound.value, args[1], args[2]);
  }
});
